import { useMemo, type ReactNode } from 'react'
import { formatDate } from '@lens/lib/decode'
import type { VersionRecord } from '@lens/types/claude'
import { DataTable, type DataTableColumn } from '../../data-table'

interface Props {
 versions: VersionRecord[]
 /** Integrated card title — forwarded to DataTable. */
 title?: ReactNode
}

const ts = (s: string | null | undefined) => (s ? new Date(s).getTime() : 0)

const columns: DataTableColumn<VersionRecord>[] = [
 {
  key: 'version',
  header:'Version',
  sort: (a, b) => a.version.localeCompare(b.version, undefined, { numeric: true }),
  render: (v) =>v.version,
 },
 {
  key: 'sessions',
  header:'Sessions',
  sort: (a, b) => a.session_count - b.session_count,
  defaultDesc: true,
  render: (v) =>v.session_count,
 },
 {
  key: 'first_seen',
  header:'First Seen',
  sort: (a, b) => ts(a.first_seen) - ts(b.first_seen),
  defaultDesc: true,
  render: (v) =>(
   <span className="text-foreground">
    {v.first_seen ? formatDate(v.first_seen) : '—'}
   </span>
  ),
 },
 {
  key: 'last_seen',
  header:'Last Seen',
  sort: (a, b) => ts(a.last_seen) - ts(b.last_seen),
  defaultDesc: true,
  render: (v) =>(
   <span className="text-foreground">
    {v.last_seen ? formatDate(v.last_seen) : '—'}
   </span>
  ),
 },
]

export function VersionHistoryTable({ versions, title }: Props) {
 const highlightedVersion = useMemo(
  () => [...versions].sort((a, b) => ts(b.last_seen) - ts(a.last_seen))[0]?.version,
  [versions],
 )

 return (
  <DataTable
   title={title}
   columns={columns}
   rows={versions}
   rowKey={(v) => v.version}
   initialSort={{ key: 'last_seen', dir: 'desc' }}
   rowClassName={(v) => (v.version === highlightedVersion ? 'text-[var(--chart-1)]' : undefined)}
   empty="No version history"
  />
 )
}
